/**
 * Auth/backend "environment set" resolution shared by client and server.
 *
 * Each environment bundles a Cognito user pool with the CRM API base URL it is
 * paired with, so the auth domain and the data plane always switch together.
 *
 * Multi-environment mode is opt-in via the NEXT_PUBLIC_AUTH_ENVIRONMENTS JSON
 * env var. When it is absent the module synthesizes a single environment from
 * the legacy env vars (NEXT_PUBLIC_COGNITO_USER_POOL_ID, ..._CLIENT_ID,
 * NEXT_PUBLIC_API_BASE_URL) so behavior is identical to the single-set setup.
 *
 * All accessors read process.env lazily (no module-load caching) so tests that
 * mutate env vars before importing dependents keep working.
 */

export interface AuthEnvironment {
  /** Stable identifier persisted client-side and used for lookups. */
  key: string;
  /** Human-readable label shown in the login selector. */
  label: string;
  cognitoUserPoolId: string;
  cognitoClientId: string;
  /** Public CRM API base URL (origin) this environment talks to. */
  apiBaseUrl: string;
}

const LEGACY_ENV_KEY = "default";

interface RawEnvironment {
  key?: unknown;
  label?: unknown;
  cognitoUserPoolId?: unknown;
  cognitoClientId?: unknown;
  apiBaseUrl?: unknown;
}

function asString(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

function parseConfiguredEnvironments(): AuthEnvironment[] {
  const raw = process.env.NEXT_PUBLIC_AUTH_ENVIRONMENTS?.trim();
  if (!raw) return [];

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    console.warn(
      "[auth-environments] NEXT_PUBLIC_AUTH_ENVIRONMENTS is not valid JSON; falling back to legacy single environment.",
    );
    return [];
  }

  if (!Array.isArray(parsed)) {
    console.warn(
      "[auth-environments] NEXT_PUBLIC_AUTH_ENVIRONMENTS must be a JSON array; falling back to legacy single environment.",
    );
    return [];
  }

  const seen = new Set<string>();
  const environments: AuthEnvironment[] = [];
  for (const entry of parsed as RawEnvironment[]) {
    const key = asString(entry.key);
    if (!key || seen.has(key)) continue;
    seen.add(key);
    environments.push({
      key,
      label: asString(entry.label) || key,
      cognitoUserPoolId: asString(entry.cognitoUserPoolId),
      cognitoClientId: asString(entry.cognitoClientId),
      apiBaseUrl: asString(entry.apiBaseUrl),
    });
  }
  return environments;
}

function legacyEnvironment(): AuthEnvironment {
  return {
    key: LEGACY_ENV_KEY,
    label: LEGACY_ENV_KEY,
    cognitoUserPoolId: asString(process.env.NEXT_PUBLIC_COGNITO_USER_POOL_ID),
    cognitoClientId: asString(
      process.env.NEXT_PUBLIC_COGNITO_USER_POOL_CLIENT_ID,
    ),
    apiBaseUrl: asString(process.env.NEXT_PUBLIC_API_BASE_URL),
  };
}

/**
 * All configured environments. Returns the legacy single environment when
 * NEXT_PUBLIC_AUTH_ENVIRONMENTS is absent/invalid.
 */
export function getAuthEnvironments(): AuthEnvironment[] {
  const configured = parseConfiguredEnvironments();
  return configured.length > 0 ? configured : [legacyEnvironment()];
}

/** True when more than one environment is configured (selector should show). */
export function isMultiEnv(): boolean {
  return getAuthEnvironments().length > 1;
}

/** The default environment (first configured / legacy). */
export function getDefaultEnvironment(): AuthEnvironment {
  return getAuthEnvironments()[0];
}

export function getEnvironmentByKey(key: string): AuthEnvironment | undefined {
  return getAuthEnvironments().find((env) => env.key === key);
}

/**
 * Cognito issuer URL for a user pool id (region is the prefix before "_").
 * Returns null when the pool id is empty/malformed.
 */
export function computeIssuer(userPoolId: string): string | null {
  if (!userPoolId) return null;
  const region = userPoolId.split("_")[0];
  if (!region) return null;
  return `https://cognito-idp.${region}.amazonaws.com/${userPoolId}`;
}

/** Resolve the environment a token belongs to by matching its `iss` claim. */
export function resolveEnvironmentByIssuer(
  issuer: string | undefined,
): AuthEnvironment | undefined {
  if (!issuer) return undefined;
  return getAuthEnvironments().find(
    (env) => computeIssuer(env.cognitoUserPoolId) === issuer,
  );
}
